import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/hooks/useAuth';
import { useLoanOfficerAuth } from '@/hooks/useLoanOfficerAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Users, MapPin, MousePointerClick, LogOut, User, 
  Phone, Mail, Building, FileText, Save, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string | null;
  created_at: string;
  town_name: string | null;
  area_name: string | null;
  request_type: string | null;
}

interface CoverageItem {
  id: string;
  town_id: string | null;
  area_id: string | null;
  town?: { name: string; slug: string } | null;
  area?: { name: string; slug: string } | null;
}

export default function LoanOfficerDashboard() {
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading } = useAuth();
  const { loanOfficer, isLoanOfficer, loading: loLoading, checked, refetch } = useLoanOfficerAuth();
  
  const [leads, setLeads] = useState<Lead[]>([]);
  const [coverage, setCoverage] = useState<CoverageItem[]>([]);
  const [adClickCount, setAdClickCount] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Profile form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_name: '',
    license_number: '',
    license_state: '',
    bio: '',
  });
  const [saving, setSaving] = useState(false);
  const [regeneratingAd, setRegeneratingAd] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  // Redirect non-loan-officers
  useEffect(() => {
    if (checked && !loLoading && !isLoanOfficer && user) {
      // User is logged in but not a loan officer
      navigate('/admin/login');
    }
  }, [checked, loLoading, isLoanOfficer, user, navigate]);

  // Populate form when loan officer data loads
  useEffect(() => {
    if (loanOfficer) {
      setFormData({
        first_name: loanOfficer.first_name || '',
        last_name: loanOfficer.last_name || '',
        email: loanOfficer.email || '',
        phone: loanOfficer.phone || '',
        company_name: loanOfficer.company_name || '',
        license_number: loanOfficer.license_number || '',
        license_state: loanOfficer.license_state || '',
        bio: loanOfficer.bio || '',
      });
      fetchStats();
    }
  }, [loanOfficer]);

  const fetchStats = async () => {
    if (!loanOfficer) return;
    
    setStatsLoading(true);
    try {
      // Fetch leads
      const { data: leadsData } = await supabase
        .from('leads')
        .select('id, name, email, phone, message, created_at, town_name, area_name, request_type')
        .eq('loan_officer_id', loanOfficer.id)
        .order('created_at', { ascending: false })
        .limit(50);
      
      setLeads(leadsData || []);

      // Fetch coverage with town/area names
      const { data: coverageData } = await supabase
        .from('loan_officer_coverage')
        .select(`
          id, town_id, area_id,
          town:towns(name, slug),
          area:areas(name, slug)
        `)
        .eq('loan_officer_id', loanOfficer.id);
      
      setCoverage((coverageData as unknown as CoverageItem[]) || []);

      // Fetch ad click count
      const { data: adsData } = await supabase
        .from('loan_officer_ads')
        .select('click_count')
        .eq('loan_officer_id', loanOfficer.id);
      
      const totalClicks = adsData?.reduce((sum, ad) => sum + ((ad as any).click_count || 0), 0) || 0;
      setAdClickCount(totalClicks);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!loanOfficer) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('loan_officers')
        .update({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          company_name: formData.company_name,
          license_number: formData.license_number,
          license_state: formData.license_state,
          bio: formData.bio,
        })
        .eq('id', loanOfficer.id);

      if (error) throw error;
      
      toast.success('Profile updated successfully');
      await refetch();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateAd = async () => {
    if (!loanOfficer) return;
    
    setRegeneratingAd(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('generate-loan-officer-ad', {
        body: { loanOfficerId: loanOfficer.id },
      });

      if (response.error) throw response.error;
      
      toast.success('Ad image regenerated successfully');
      await refetch();
    } catch (error) {
      console.error('Error regenerating ad:', error);
      toast.error('Failed to regenerate ad image');
    } finally {
      setRegeneratingAd(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login');
  };

  const isLoading = authLoading || loLoading || !checked;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !isLoanOfficer || !loanOfficer) {
    return null;
  }

  const fullName = `${loanOfficer.first_name} ${loanOfficer.last_name}`.trim() || 'Loan Officer';

  return (
    <div className="min-h-screen bg-muted">
      <Helmet>
        <title>Dashboard | {fullName}</title>
      </Helmet>

      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Building className="h-5 w-5 text-primary" />
              <span className="font-display text-lg">{fullName}</span>
              <Badge variant="secondary" className="text-xs">Loan Officer</Badge>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground hidden sm:block">{user.email}</span>
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Leads</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsLoading ? '...' : leads.length}</div>
              <p className="text-xs text-muted-foreground">Assigned to you</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Coverage</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsLoading ? '...' : coverage.length}</div>
              <p className="text-xs text-muted-foreground">Towns & areas</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Ad Clicks</CardTitle>
              <MousePointerClick className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statsLoading ? '...' : adClickCount}</div>
              <p className="text-xs text-muted-foreground">Total clicks</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="leads" className="space-y-4">
          <TabsList>
            <TabsTrigger value="leads">Leads</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="coverage">Coverage</TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <Card>
              <CardHeader>
                <CardTitle>Your Leads</CardTitle>
                <CardDescription>Leads captured from pages featuring your ad</CardDescription>
              </CardHeader>
              <CardContent>
                {leads.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No leads yet</p>
                ) : (
                  <div className="space-y-4">
                    {leads.map((lead) => (
                      <div key={lead.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">{lead.name}</h4>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                              <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-primary">
                                <Mail className="h-3 w-3" />
                                {lead.email}
                              </a>
                              {lead.phone && (
                                <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-primary">
                                  <Phone className="h-3 w-3" />
                                  {lead.phone}
                                </a>
                              )}
                            </div>
                            {lead.message && (
                              <p className="text-sm mt-2 text-muted-foreground">{lead.message}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(lead.created_at), 'MMM d, yyyy')}
                            </p>
                            {(lead.town_name || lead.area_name) && (
                              <Badge variant="outline" className="mt-1 text-xs">
                                {lead.area_name || lead.town_name}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription>Update your contact details and bio</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="license_number">NMLS #</Label>
                      <Input
                        id="license_number"
                        value={formData.license_number}
                        onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="license_state">State</Label>
                      <Input
                        id="license_state"
                        value={formData.license_state}
                        onChange={(e) => setFormData({ ...formData, license_state: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      rows={4}
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      placeholder="Tell potential clients about yourself..."
                    />
                  </div>

                  <Button onClick={handleSaveProfile} disabled={saving} className="w-full">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Profile
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Your Ad Image</CardTitle>
                  <CardDescription>This appears on mortgage and town pages</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loanOfficer.ad_image_url ? (
                    <img
                      src={loanOfficer.ad_image_url}
                      alt="Your ad"
                      className="w-full rounded-lg border"
                    />
                  ) : (
                    <div className="aspect-square bg-muted rounded-lg flex items-center justify-center">
                      <p className="text-muted-foreground text-sm">No ad image yet</p>
                    </div>
                  )}

                  <Button 
                    variant="outline" 
                    onClick={handleRegenerateAd} 
                    disabled={regeneratingAd}
                    className="w-full"
                  >
                    {regeneratingAd ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Regenerate Ad Image
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Coverage Tab */}
          <TabsContent value="coverage">
            <Card>
              <CardHeader>
                <CardTitle>Your Coverage Areas</CardTitle>
                <CardDescription>Towns and areas where your ad appears</CardDescription>
              </CardHeader>
              <CardContent>
                {coverage.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No coverage areas assigned yet. Contact your site administrator.
                  </p>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                    {coverage.map((item) => (
                      <div key={item.id} className="border rounded-lg p-3 flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {item.area?.name || item.town?.name || 'Unknown location'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
